import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle, RefreshCw, Trophy, Share2, Download, X, ScrollText, Images } from 'lucide-react';
import { EtatJeu, GameData, Maj } from '../game/types';
import { tempsFinalSec } from '../game/state';
import { calculerJeton, deobf, formatTemps, assetUrl } from '../game/logic';
import { envoyerScore } from '../game/api';
import { Petales } from '../components/Petales';
import { BestOf } from '../components/BestOf';
import { BrancheFlorale } from '../components/Florals';

interface Props {
  data: GameData;
  etat: EtatJeu;
  maj: Maj;
  onReset: () => void;
}

type Statut = 'envoi' | 'ok' | 'conflit' | 'echec';

export default function Fin({ data, etat, maj, onReset }: Props) {
  const temps = tempsFinalSec(etat);
  const [statut, setStatut] = useState<Statut>(etat.scoreEnvoye ? 'ok' : 'envoi');
  const [pseudo, setPseudo] = useState(etat.pseudoFinal || etat.prenom);
  const [certificat, setCertificat] = useState<string | null>(null);
  const [bestOfOuvert, setBestOfOuvert] = useState(false);
  const envoiEnCours = useRef(false);
  const waOuvert = useRef(false);

  const lienWhatsApp = useCallback(() => {
    const penalites =
      etat.penalitesSec > 0 ? `(pénalités : ${Math.round(etat.penalitesSec / 60)} min)` : '(sans pénalité)';
    const message = `🏰 SAINT-SENOCH — ${etat.prenom} a terminé\nTemps : ${formatTemps(temps)} ${penalites}\nMots trouvés : ${etat.serruresOuvertes.length}/3\nJeton : ${etat.jeton || ''}`;
    return `https://wa.me/${data.config.whatsappGreffier}?text=${encodeURIComponent(message)}`;
  }, [data.config.whatsappGreffier, etat, temps]);

  const soumettre = useCallback(
    async (pseudoAEnvoyer: string) => {
      if (envoiEnCours.current || etat.scoreEnvoye) return;
      envoiEnCours.current = true;
      setStatut('envoi');
      let jeton = etat.jeton;
      if (!jeton) {
        jeton = await calculerJeton(etat.prenom, etat.demarreLe, temps, data.config.selJeton);
        maj((e) => ({ ...e, jeton }));
      }
      const rep = await envoyerScore(
        {
          pseudo: pseudoAEnvoyer.trim(),
          tempsSec: temps,
          penalitesSec: etat.penalitesSec,
          motsTrouves: etat.serruresOuvertes.length,
          jeton,
        },
        data.config.apiTimeoutMs
      );
      envoiEnCours.current = false;
      if (rep.ok) {
        maj((e) => ({ ...e, scoreEnvoye: true, rang: rep.rang ?? null, pseudoFinal: pseudoAEnvoyer.trim() }));
        setStatut('ok');
        toast.success('Votre temps est inscrit au classement !');
      } else if (rep.conflit) {
        const suggestion = rep.suggestion || `${etat.prenom} #${jeton}`;
        setPseudo(suggestion);
        setStatut('conflit');
      } else {
        setStatut('echec');
        if (!waOuvert.current) {
          waOuvert.current = true;
          window.open(lienWhatsApp(), '_blank');
        }
      }
    },
    [data.config, etat, maj, temps, lienWhatsApp]
  );

  useEffect(() => {
    if (!etat.scoreEnvoye) soumettre(etat.prenom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const surRetourReseau = () => {
      if (!etat.scoreEnvoye && statut === 'echec') soumettre(pseudo);
    };
    window.addEventListener('online', surRetourReseau);
    return () => window.removeEventListener('online', surRetourReseau);
  }, [etat.scoreEnvoye, statut, pseudo, soumettre]);

  const genererCertificat = async () => {
    try {
      await Promise.all([
        document.fonts.load('150px Sacramento'),
        document.fonts.load('600 60px Montserrat'),
        document.fonts.load('36px Montserrat'),
      ]);
    } catch {
      /* les polices système prendront le relais */
    }
    const c = document.createElement('canvas');
    c.width = 1080;
    c.height = 1920;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, 1080, 1920);
    const filigrane = await new Promise<HTMLImageElement | null>((resolve) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => resolve(null);
      im.src = assetUrl('img/bestof/maries.webp');
    });
    if (filigrane) {
      ctx.save();
      ctx.globalAlpha = 0.13;
      const echelle = Math.max(1080 / filigrane.width, 1920 / filigrane.height);
      const fw = filigrane.width * echelle;
      const fh = filigrane.height * echelle;
      ctx.drawImage(filigrane, (1080 - fw) / 2, (1920 - fh) / 2, fw, fh);
      ctx.restore();
    }
    ctx.strokeStyle = '#2B3A63';
    ctx.lineWidth = 3;
    ctx.strokeRect(48, 48, 984, 1824);
    ctx.lineWidth = 1;
    ctx.strokeRect(66, 66, 948, 1788);

    const petaleCoin = (cx: number, cy: number, rot: number) => {
      for (let i = 0; i < 5; i++) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rot + (i * Math.PI * 2) / 5);
        ctx.fillStyle = i % 2 ? 'rgba(126,146,180,0.4)' : 'rgba(168,187,212,0.55)';
        ctx.beginPath();
        ctx.ellipse(0, -34, 16, 32, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      ctx.fillStyle = 'rgba(43,58,99,0.65)';
      ctx.beginPath();
      ctx.arc(cx, cy, 9, 0, Math.PI * 2);
      ctx.fill();
    };
    petaleCoin(160, 170, 0.4);
    petaleCoin(920, 1750, 2.1);

    ctx.textAlign = 'center';
    ctx.fillStyle = '#2B3A63';
    ctx.font = '130px Sacramento';
    ctx.fillText('B & W', 540, 310);
    ctx.fillStyle = '#465277';
    ctx.font = '500 34px Montserrat';
    ctx.fillText("LE JEU DE L'APÉRO — TATOO B/W", 540, 400);

    ctx.fillStyle = '#3C3C3C';
    ctx.font = '36px Montserrat';
    ctx.fillText('Céans est attesté que', 540, 640);
    ctx.fillStyle = '#2B3A63';
    ctx.font = '160px Sacramento';
    ctx.fillText(etat.prenom, 540, 830);
    ctx.fillStyle = '#3C3C3C';
    ctx.font = '38px Montserrat';
    ctx.fillText('a percé les trois secrets de Saint-Senoch', 540, 950);
    ctx.font = '34px Montserrat';
    ctx.fillText('en un temps de', 540, 1090);
    ctx.fillStyle = '#2B3A63';
    ctx.font = '600 190px Montserrat';
    ctx.fillText(formatTemps(temps), 540, 1290);
    ctx.fillStyle = '#465277';
    ctx.font = '30px Montserrat';
    ctx.fillText(
      etat.penalitesSec > 0 ? `dont ${Math.round(etat.penalitesSec / 60)} min de pénalités` : 'sans la moindre pénalité',
      540,
      1360
    );
    ctx.fillStyle = '#2B3A63';
    ctx.font = '600 40px Montserrat';
    ctx.fillText(data.motscles.map((m) => deobf(m.reponseObf)).join(' · '), 540, 1480);
    ctx.font = '90px Sacramento';
    ctx.fillText('Château de Saint-Senoch', 540, 1650);
    ctx.fillStyle = '#465277';
    ctx.font = '34px Montserrat';
    ctx.fillText(data.config.dateMariage, 540, 1730);

    setCertificat(c.toDataURL('image/png'));
  };

  const partagerCertificat = async () => {
    if (!certificat) return;
    try {
      const blob = await (await fetch(certificat)).blob();
      const fichier = new File([blob], 'certificat-tatoo-bw.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [fichier] })) {
        await navigator.share({ files: [fichier], title: 'Tatoo B/W' });
        return;
      }
    } catch {
      /* repli : téléchargement */
    }
    telechargerCertificat();
  };

  const telechargerCertificat = () => {
    if (!certificat) return;
    const a = document.createElement('a');
    a.href = certificat;
    a.download = 'certificat-tatoo-bw.png';
    a.click();
  };

  return (
    <div className="min-h-screen bg-white">
      <Petales nombre={20} />
      <div className="max-w-md mx-auto px-6 py-10 pb-16">
        <p className="petit-cap text-center anim-apparition">La quête est achevée</p>
        <h1 className="font-script text-encre text-5xl text-center mt-3 anim-apparition">Les trois secrets sont percés</h1>
        <p className="text-center text-texte italic whitespace-pre-line leading-relaxed mt-6 anim-apparition" style={{ animationDelay: '0.15s' }}>
          {data.config.texteFinal}
        </p>
        <p className="text-center text-encre font-semibold tracking-[0.18em] text-sm mt-4">
          {data.motscles.map((m) => deobf(m.reponseObf)).join(' · ')}
        </p>

        <div className="mt-10 border border-encre rounded-lg py-8 px-4 text-center bg-white">
          <p className="petit-cap">Votre temps</p>
          <p className="text-encre font-semibold tabular-nums leading-none mt-3 text-[76px] sm:text-8xl" data-testid="temps-final">
            {formatTemps(temps)}
          </p>
          <p className="text-sm text-encre-clair mt-3">
            {etat.penalitesSec > 0
              ? `dont ${Math.round(etat.penalitesSec / 60)} min de pénalités`
              : 'sans la moindre pénalité'}
            {etat.jeton ? ` · jeton ${etat.jeton}` : ''}
          </p>
          <p className="text-xs text-encre-clair mt-2">En cas de besoin, montrez cet écran au greffier.</p>
        </div>

        <div className="mt-6" data-testid="statut-envoi">
          {statut === 'envoi' && (
            <div className="carte px-5 py-4 flex items-center gap-3">
              <RefreshCw size={18} className="text-encre-clair animate-spin" />
              <p className="text-sm text-texte">Envoi de votre temps au classement…</p>
            </div>
          )}

          {statut === 'ok' && (
            <div className="border border-succes rounded-lg px-5 py-5 text-center">
              <p className="text-succes font-medium">Vous figurez au classement</p>
              {etat.rang && (
                <p className="text-encre font-semibold text-4xl mt-2" data-testid="rang-joueur">
                  {etat.rang}
                  <span className="text-lg align-super">{etat.rang === 1 ? 'ᵉʳ' : 'ᵉ'}</span>
                </p>
              )}
              <Link to="/classement" data-testid="voir-classement-btn" className="btn-principal mt-4">
                <Trophy size={18} /> Voir le classement en direct
              </Link>
            </div>
          )}

          {statut === 'conflit' && (
            <div className="border border-erreur rounded-lg px-5 py-5">
              <p className="text-erreur font-medium text-sm">
                Ce prénom est déjà pris au classement. Validez le pseudo proposé, ou choisissez-en un autre.
              </p>
              <input
                data-testid="pseudo-conflit-input"
                value={pseudo}
                onChange={(e) => setPseudo(e.target.value)}
                maxLength={40}
                className="mt-3 w-full min-h-[52px] px-4 rounded-lg border border-encre bg-white text-texte"
              />
              <button
                data-testid="pseudo-conflit-valider-btn"
                className="btn-principal mt-3"
                disabled={pseudo.trim().length < 2}
                onClick={() => soumettre(pseudo)}
              >
                Valider ce pseudo
              </button>
            </div>
          )}

          {statut === 'echec' && (
            <div className="border border-erreur rounded-lg px-5 py-5">
              <p className="text-erreur font-medium text-sm">
                Le réseau se dérobe — WhatsApp prend le relais auprès du greffier.
              </p>
              <a href={lienWhatsApp()} target="_blank" rel="noreferrer" data-testid="whatsapp-btn" className="btn-principal mt-4">
                <MessageCircle size={18} /> Envoyer par WhatsApp
              </a>
              <button data-testid="reessayer-envoi-btn" className="btn-secondaire mt-3" onClick={() => soumettre(pseudo)}>
                <RefreshCw size={18} /> Réessayer l'envoi au classement
              </button>
            </div>
          )}
        </div>

        <button data-testid="certificat-btn" className="btn-secondaire mt-6" onClick={genererCertificat}>
          <ScrollText size={18} /> Générer mon certificat
        </button>

        {data.bestof.length > 0 && (
          <button data-testid="bestof-btn" className="btn-secondaire mt-3" onClick={() => setBestOfOuvert(true)}>
            <Images size={18} /> Le best of en photos
          </button>
        )}

        <div className="flex justify-center mt-10">
          <BrancheFlorale className="w-52" />
        </div>

        <button
          data-testid="recommencer-btn"
          className="mt-8 mx-auto block min-h-[44px] text-sm text-encre-clair underline underline-offset-4"
          onClick={() => {
            if (window.confirm('Effacer cette partie et recommencer ?')) onReset();
          }}
        >
          Recommencer une partie
        </button>
      </div>

      {bestOfOuvert && <BestOf photos={data.bestof} onFermer={() => setBestOfOuvert(false)} />}

      {certificat && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="certificat-overlay">
          <div className="max-w-md mx-auto px-6 py-6">
            <button
              data-testid="fermer-certificat-btn"
              onClick={() => setCertificat(null)}
              className="min-h-[44px] flex items-center gap-2 text-encre-clair font-medium"
            >
              <X size={20} /> Fermer
            </button>
            <img src={certificat} alt="Certificat" className="w-full border border-encre mt-3" />
            <div className="flex gap-3 mt-4">
              <button data-testid="partager-certificat-btn" className="btn-principal flex-1" onClick={partagerCertificat}>
                <Share2 size={18} /> Partager
              </button>
              <button data-testid="telecharger-certificat-btn" className="btn-secondaire flex-1" onClick={telechargerCertificat}>
                <Download size={18} /> Télécharger
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
